'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Accommodation, Gender } from '@prisma/client';
import { IconMail } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useParams, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import type { z } from 'zod';
import FormInput from '@/components/forms/form-input';
import { Form } from '@/components/ui/form';
import { bookingSchema } from '@/schemas';
import { api } from '@/server/callers/client';
import { trackEvent } from '@/utils/analytics';
import { usePageViewTracking } from '@/utils/hooks';

type FormData = z.infer<typeof bookingSchema>;

interface TripItem {
  id: string;
  startDate: Date;
  endDate: Date;
  price: number;
}

const parseNumber = (value: string | number) => {
  if (typeof value === 'number') {
    return value;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 1 : parsed;
};

export default function BookingForm() {
  usePageViewTracking();

  const params = useParams<{ destination: string; trip: string }>();
  const searchParams = useSearchParams();
  const tripItemId = searchParams.get('tripItemId');
  const startDateParam = searchParams.get('startDate');
  const tripSlugParam = searchParams.get('tripSlug');

  // Determine booking mode
  const isCustomDate = !tripItemId && !!startDateParam && !!tripSlugParam;

  const form = useForm<FormData>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      fullName: '',
      nationality: '',
      gender: 'Male',
      email: '',
      phone: '',
      message: '',
      tripItemId: tripItemId || undefined,
      startDate: startDateParam || undefined,
      tripSlug: tripSlugParam || undefined,
      numberOfVisitors: 1,
      accommodation: Object.values(Accommodation)[0],
    },
  });

  // Get trip data
  const { data: tripData, isLoading: isTripLoading } = api.trips.get.useQuery({
    slug: params.trip as string,
  });

  // For TripItem flow: validate and set tripItemId
  useEffect(() => {
    if (!isCustomDate && tripData?.items && tripData.items.length > 0) {
      if (tripItemId) {
        const validTripItem = tripData.items.find((item) => item.id === tripItemId);
        if (validTripItem) {
          form.setValue('tripItemId', tripItemId);
        } else {
          form.setValue('tripItemId', undefined);
        }
      } else {
        form.setValue('tripItemId', undefined);
      }
    }
  }, [tripData, form, tripItemId, isCustomDate]);

  // For custom date flow: set startDate and tripSlug
  useEffect(() => {
    if (isCustomDate) {
      form.setValue('startDate', startDateParam);
      form.setValue('tripSlug', tripSlugParam);
    }
  }, [isCustomDate, startDateParam, tripSlugParam, form]);

  const bookMutation = api.bookings.create.useMutation({
    onSuccess: () => {
      trackEvent({
        action: 'booking_form_submit_success',
        category: 'forms',
        label: 'Booking Form Success',
        value: 1,
      });

      toast.success(
        'Thank you for your booking request! We will get back to you soon.',
      );
      form.reset();
    },
    onError: (error) => {
      trackEvent({
        action: 'booking_form_submit_error',
        category: 'forms',
        label: error.message || 'Unknown error',
        value: 1,
      });

      toast.error(error.message || 'Something went wrong. Please try again.');
    },
  });

  // Track form interaction
  const hasInteracted = useRef(false);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (!hasInteracted.current && name && value) {
        hasInteracted.current = true;

        trackEvent({
          action: 'booking_form_started',
          category: 'forms',
          label: 'Booking Form Interaction',
          value: 1,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const handleSubmit = async (data: FormData) => {
    trackEvent({
      action: 'booking_form_submit',
      category: 'forms',
      label: 'Booking Form',
      value: 1,
    });

    bookMutation.mutate(data);
  };

  if (isTripLoading) {
    return (
      <div className="animate-pulse p-6 bg-gray-100 rounded-lg">
        Loading trip options...
      </div>
    );
  }

  // For TripItem flow, require items. For custom date flow, just need trip data.
  if (!tripData) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-red-600">
        Trip not found. Please check back later.
      </div>
    );
  }

  const hasTripItems = tripData.items.length > 0;

  // Block if neither custom date nor trip items available
  if (!isCustomDate && !hasTripItems) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-red-600">
        No available trip dates found. Please check back later.
      </div>
    );
  }

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Form {...form}>
      <form
        data-aos="fade-up"
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-6"
      >
        {/* Trip Date Display */}
        <h3 className="text-xl font-medium">Your Trip Date</h3>

        {isCustomDate && startDateParam ? (
          <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
            <p className="text-sm text-gray-500">Selected date</p>
            <p className="text-lg font-semibold">
              {dayjs(startDateParam).format('dddd, MMMM D, YYYY')}
            </p>
            {tripData.duration > 0 && (
              <p className="text-sm text-gray-500 mt-1">
                Duration: {tripData.duration}{' '}
                {tripData.duration === 1 ? 'day' : 'days'}
              </p>
            )}
          </div>
        ) : hasTripItems ? (
          <FormInput
            id="tripItemId"
            label="Trip Date"
            type="select"
            required
            options={tripData.items.map((item: TripItem) => ({
              value: item.id,
              label: `${formatDate(item.startDate)} to ${formatDate(item.endDate)} - $${item.price}`,
            }))}
            form={form}
          />
        ) : null}

        {/* Personal Information */}
        <h3 className="text-xl font-medium pt-4">Personal Information</h3>

        <FormInput
          type="text"
          id="fullName"
          label="Full Name"
          placeholder="Your full name"
          required
          form={form}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormInput
            type="text"
            id="nationality"
            label="Nationality"
            placeholder="Your nationality"
            required
            form={form}
          />

          <FormInput
            type="select"
            id="gender"
            label="Gender"
            required
            options={Object.values(Gender).map((gender) => ({
              value: gender,
              label: gender,
            }))}
            form={form}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormInput
            id="email"
            label="Email"
            type="email"
            required
            placeholder="your.email@example.com"
            form={form}
          />

          <FormInput
            id="phone"
            label="Phone"
            type="tel"
            required
            placeholder="Your phone number"
            form={form}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormInput
            id="numberOfVisitors"
            label="Number of Visitors"
            type="number"
            required
            form={form}
            middleware={parseNumber}
          />

          <FormInput
            id="accommodation"
            label="Accommodation Type"
            type="select"
            required
            options={Object.values(Accommodation).map((type) => ({
              value: type,
              label: type,
            }))}
            form={form}
          />
        </div>

        <FormInput
          id="message"
          label="Special Requests"
          type="textarea"
          placeholder="Any special requirements or information you'd like us to know"
          rows={5}
          form={form}
        />

        {bookMutation.isSuccess && (
          <div className="flex flex-col justify-center items-center gap-2 p-4 bg-green-100 rounded-lg text-green-800">
            <IconMail className="text-green-800" size={90} />
            <p className="text-lg">
              You have successfully submitted your booking request. Please check your
              email for verification. You can also check your spam folder.
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={bookMutation.isPending}
          className="bg-accent text-black font-medium rounded-lg w-fit px-12 py-3 hover:bg-accent/90 transition-colors disabled:bg-blue-400"
        >
          {bookMutation.isPending ? 'Processing...' : 'Book Now'}
        </button>
      </form>
    </Form>
  );
}
